import React, { useEffect, useState, ReactElement } from "react";
import { Form, Formik } from "formik";
import { object, string } from "yup";
import { isValidPhoneNumber } from "react-phone-number-input";
import { screenMeta } from "../constants";
import Contact from "./Contact";
import Documents from "./Documents";
import Footer from "./partials/Footer";
import Header from "./partials/Header";
import Review from "./Review";
import Subscribe from "./Subscribe";
import ThankYou from "./ThankYou";
import { validateEmail } from "../../EmailField/EmailField";
import { JobCategory, JobLocation, TrackingObject } from "../DirectApplyTypes";
import { getStorageApplicant, setStorageApplicant } from "../DirectApplyUtils";
interface ScreensProps {
    companyId: string;
    companyName: string;
    endpoint: string;
    hideModal: () => void;
    atsJobId: string;
    jobName: string;
    jobId?: number;
    jobCategory: JobCategory | null;
    normalizedTitle: string;
    level: string;
    link: string;
    locations: JobLocation[];
    setStep: (value: number | ((prevVar: number) => number)) => void;
    step: number;
    signUpCallBack: (props: any) => Promise<boolean>;
    snowplowId: string;
    service: string;
    onClose: () => void;
    trackingProps: TrackingObject;
    showMoreJobsButton?: boolean;
}

const dataUriRegExp = /^([a-z0-9!$&',()*+;=\-._~:@/?%\s]*)$/i;

/**
 * Contains Yep validation schema.
 **/
const ContactSchema = object().shape({
    firstName: string().required("First Name is Required."),
    lastName: string().required("Last Name is Required"),
    email: string()
    .test('validator-custom-email', function (value) {
            return validateEmail(value ?? "")
                ? true
                : this.createError({
                    path: this.path,
                    message: "Invalid email format, the correct format is 'name@emailprovider.com'.",
                });
        })
        .required("Email is required."),
    phone: string()
        .trim()
        .test('validator-custom-phone', function (value) {
            return isValidPhoneNumber(value ?? "")
                ? true
                : this.createError({
                    path: this.path,
                    message: "Invalid phone number format, correct format is '+1 646 567-5667'.",
                });
          })
        .required("Phone is required."),
    resume: string()
        .trim()
        .matches(dataUriRegExp, "Resume must be a valid file.")
        .required("Resume is required."),
    coverLetter: string().trim()
        .matches(dataUriRegExp, "Cover Letter must be a valid file.")
        .nullable()
        .notRequired(),
    location: string()
        .trim()
        .min(4, "Invalid location format.")
        .required("Location is required.")
});

/**
 * Renders a multi-step job application form. It receives props containing information about the job and company, and
 * it manages the state of the form as the user progresses through the steps. The main objective of the function is to
 * provide a user-friendly interface for job applicants to submit their contact information, resume, and cover letter.
 *
 * @returns {ReactElement} The direct apply screens.
 */
const Screens = (props: ScreensProps): ReactElement => {
    const {
        companyName,
        endpoint,
        hideModal,
        jobName,
        jobId,
        jobCategory,
        normalizedTitle,
        level,
        link,
        locations,
        setStep,
        step,
        signUpCallBack,
        snowplowId,
        companyId,
        atsJobId,
        service,
        onClose,
        trackingProps,
        showMoreJobsButton
    } = props;

    const steps = screenMeta.length;
    const [userProps, setUserProps] = useState({});
    const [isRepeatUser, setIsRepeatUser] = useState(false);

    useEffect(() => {
        const applicant = getStorageApplicant();

        if (applicant?.firstName) {
            setIsRepeatUser(true);
            // Use the stored applicant
            setUserProps(applicant);
            // update the applicants timeout
            setStorageApplicant(applicant);
        }
    }, []);

    const fields = {
        firstName: "",
        lastName: "",
        email: "",
        phone: "",
        resume: "",
        resumeName: "",
        resumeStats: "",
        coverLetter: "",
        coverLetterName: "",
        coverLetterStats: "",
        alerts: "true",
        newsletter: "true",
        location: locations[0]?.value ?? "",
        ...userProps
    };

    // Everything needed to drive the footer.
    const footerProps = {
        atsJobId,
        companyId,
        endpoint,
        hideModal,
        jobId,
        jobCategory,
        level,
        link,
        normalizedTitle,
        onClose,
        service,
        signUpCallBack,
        snowplowId,
        step,
        stepBack: () => setStep(Math.min(step - 1, steps)),
        stepForward: () => setStep(Math.min(step + 1, steps)),
        steps,
        trackingProps,
        showMoreJobsButton
    };

    // Everything needed to drive the header.
    const headerProps = {
        companyName,
        isRepeatUser,
        jobName,
        locations,
        step,
        steps: 4,
    };

    return (
        <Formik
            enableReinitialize={true}
            initialValues={fields}
            validationSchema={ContactSchema}
            onSubmit={() => { }}
        >
            {() => (
                <>
                    <Header {...headerProps} />
                    <Form
                        noValidate={true}
                        aria-label="Direct Job Apply Form"
                    >
                        <Subscribe {...{
                            companyName,
                            locations,
                            jobCategory,
                            enabled: step === 1,
                        }}
                        />
                        <Contact {...{
                            enabled: step === 2
                        }} />
                        <Documents {...{
                            enabled: step === 3,
                        }} />
                        <Review {...{
                            companyName,
                            enabled: step === 4,
                        }} />
                        <ThankYou {...{
                            companyName,
                            enabled: step === 5,
                        }} />
                    </Form>
                    <Footer {...footerProps} />
                </>
            )}
        </Formik>
    );
};
Screens.displayName = "Screens";

export default Screens;
export type { ScreensProps };
